// frontend/src/components/Sidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  FiHome, FiFileText, FiZap, FiUser, FiLogOut,
  FiChevronDown, FiSettings, FiGrid, FiTarget, FiCreditCard, FiTrendingUp, FiPieChart, FiShield, FiUserPlus
} from 'react-icons/fi';
import { FaEuroSign, FaFire } from 'react-icons/fa';
import logo from '../assets/logo.png';

// ─── Helper central ───────────────────────────────────────────────────────────
const isAdminLike = (userProfile) => {
  const rang = Number(userProfile?.rang ?? -1);
  return rang === 0 || rang === 8;
};

// ─── NavItem ──────────────────────────────────────────────────────────────────
const NavItem = ({ to, icon, children, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
        isActive
          ? 'bg-blue-50 text-blue-700'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`
    }
  >
    <span className="mr-3 text-lg">{icon}</span>
    <span className="flex-1">{children}</span>
  </NavLink>
);

// ─── ProfileMenu ──────────────────────────────────────────────────────────────
const ProfileMenu = ({ userName, userProfile }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate       = useNavigate();
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target))
        setIsProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsProfileOpen(false);
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Erreur déconnexion :', error.message);
    navigate('/');
  };

  if (!userProfile) return null;

  return (
    <div className="relative" ref={profileMenuRef}>
      <button
        onClick={() => setIsProfileOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left rounded-lg p-2 text-sm
                   text-gray-600 hover:bg-gray-50 hover:text-gray-900
                   focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center space-x-2 truncate">
          <FiUser className="h-5 w-5 flex-shrink-0 text-gray-500" />
          <span className="font-medium truncate">{userName || 'Mon Compte'}</span>
        </div>
        <FiChevronDown className={`h-4 w-4 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
      </button>

      {isProfileOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-full origin-bottom-left rounded-md
                        bg-white shadow-lg ring-1 ring-gray-200 z-20 border border-gray-200">
          <div className="py-1">
            <div className="block px-4 py-2 text-sm text-gray-600 border-b border-gray-200">
              Connecté en tant que :<br />
              <span className="font-medium text-gray-900 truncate">{userProfile.email}</span>
            </div>
            <Link
              to="/dashboard/profil"
              onClick={() => setIsProfileOpen(false)}
              className="flex w-full items-center space-x-2 px-4 py-2 text-sm
                         text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <FiSettings className="h-4 w-4" />
              <span>Mon Compte</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex w-full items-center space-x-2 px-4 py-2 text-sm
                         text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <FiLogOut className="h-4 w-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sidebar principale ───────────────────────────────────────────────────────
const Sidebar = ({ userRole, isSidebarOpen, onLinkClick, userName, userProfile }) => {

  const adminMode = isAdminLike(userProfile);

  // ✅ rang 0 = super-admin | rang 8 = auditeur
  const adminLinks = [
    { to: '/dashboard/admin',              icon: <FiGrid />,       label: "Vue d'ensemble"  },
    { to: '/dashboard/pdl-dashboard',      icon: <FiZap />,        label: 'PDL'             },
    { to: '/dashboard/pce-dashboard',      icon: <FiTarget />,     label: 'PCE'             },
    { to: '/dashboard/admin/analyse-turpe', icon: <FiFileText />,   label: 'Acheminement'   },
    { to: '/dashboard/admin/prospect-cspe', icon: <FiUserPlus />,   label: 'Prospect CSPE'  },
    { to: '/dashboard/admin/analyse-cspe',  icon: <FiZap />,        label: 'Taxes (CSPE)'   },
    { to: '/dashboard/admin/analyse-ticgn', icon: <FaFire />,      label: 'Taxes (TICGN)'  },
    { to: '/dashboard/admin/audit-enedis',  icon: <FiShield />,     label: 'Audit ENEDIS'   },
    { to: '/dashboard/admin/revenue', icon: <FiTrendingUp />, label: 'Revenus' },
    { to: '/dashboard/admin/bank',         icon: <FiCreditCard />, label: 'Prélèvements'   }, // ← NOUVEAU
    { to: '/dashboard/admin/lcl',          icon: <FiCreditCard />, label: 'Export LCL SEPA' },
    { to: '/dashboard/admin/cre-updates',  icon: <FiZap />,        label: 'Updates CRE'     },
    // Bilan énergétique : strictement rang 0 (super-admin), pas rang 8 (auditeur).
    ...(Number(userProfile?.rang) === 0
      ? [{ to: '/dashboard/admin/bilan',   icon: <FiPieChart />,   label: 'Bilan énergétique' }]
      : []),
  ];

  const userLinks = [
    { to: '/dashboard',           icon: <FiHome />,     label: 'Tableau de bord'               },
    { to: '/dashboard/pdl',       icon: <FiZap />,      label: 'Points de Livraison'           },
    { to: '/dashboard/pce',       icon: <FiTarget />,   label: 'Points de Référence de Mesure' },
    { to: '/dashboard/documents', icon: <FiFileText />, label: 'Mes Documents'                 },
  ];

  const links  = adminMode ? adminLinks : userLinks;
  const logoTo = adminMode ? '/dashboard/admin' : '/dashboard';

  const renderLinks = (isMobile = false) =>
    links.map((link) => (
      <NavItem
        key={link.to}
        to={link.to}
        icon={link.icon}
        onClick={isMobile ? onLinkClick : undefined}
      >
        {link.label}
      </NavItem>
    ));

  return (
    <>
      {/* ── Desktop ───────────────────────────────────────────────────────── */}
      <aside className="hidden md:flex md:flex-col md:w-56 bg-white text-gray-800 border-r border-gray-200">
        <div className="flex flex-col flex-grow p-4">

          <div className="flex items-center justify-center h-16 mb-4">
            <Link to={logoTo}>
              <img src={logo} alt="Logo Energy Group" className="w-full" />
            </Link>
          </div>

          <nav className="flex-grow space-y-1">
            {renderLinks()}
          </nav>

          <div className="pt-4 mt-auto border-t border-gray-200">
            <ProfileMenu userName={userName} userProfile={userProfile} />
          </div>

        </div>
      </aside>

      {/* ── Mobile ────────────────────────────────────────────────────────── */}
      <aside className={`md:hidden fixed inset-y-0 left-0 z-30 w-64 bg-white text-gray-800
                         border-r border-gray-200 shadow-lg transform transition-transform
                         duration-300 ease-in-out
                         ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col flex-grow p-4 space-y-2">

          <div className="flex items-center justify-center h-16 mb-4 mt-8">
            <Link to={logoTo} onClick={onLinkClick}>
              <img src={logo} alt="Logo Energy Group" className="w-full" />
            </Link>
          </div>

          <nav className="flex-grow space-y-1">
            {renderLinks(true)}
          </nav>

          <div className="pt-4 mt-auto border-t border-gray-200">
            <ProfileMenu userName={userName} userProfile={userProfile} />
          </div>

        </div>
      </aside>
    </>
  );
};

export default Sidebar;
